import React, { useState } from 'react';
import './FarsiKeyboard.css';

interface FarsiKeyboardProps {
  onInputChange: (text: string) => void;
  currentText: string;
  placeholder?: string;
}

const FarsiKeyboard: React.FC<FarsiKeyboardProps> = ({
  onInputChange,
  currentText,
  placeholder = "Type in Farsi..."
}) => {
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isAltPressed, setIsAltPressed] = useState(false);
  const [showVowels, setShowVowels] = useState(false);

  // Farsi keyboard layout with Persian vowels (not Arabic)
  const farsiKeys = [
    // First row - Persian vowels (diacritics)
    ['َ', 'ِ', 'ُ', 'ً', 'ٍ', 'ٌ', 'ّ', 'ْ', 'ٰ', 'ٱ'],
    // Second row
    ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'چ'],
    // Third row
    ['ش', 'س', 'ی', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ک', 'گ'],
    // Fourth row
    ['ظ', 'ط', 'ز', 'ر', 'ذ', 'د', 'پ', 'و', 'ث', 'ژ'],
    // Fifth row (numbers and symbols)
    ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۰']
  ];

  // English numbers for shift
  const englishNumbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

  // Special characters
  const specialChars = ['؟', '!', '@', '#', '$', '%', '^', '&', '*', '('];

  // Persian vowel explanations (updated for Persian/Farsi)
  const vowelExplanations = {
    'َ': 'Fatha (a) - Persian short a sound like in "dad"',
    'ِ': 'Kasra (i) - Persian short i sound like in "set"', 
    'ُ': 'Damma (u) - Persian short u sound like in "border"',
    'ً': 'Tanwin Fatha (an) - Persian indefinite ending -an',
    'ٍ': 'Tanwin Kasra (in) - Persian indefinite ending -in',
    'ٌ': 'Tanwin Damma (un) - Persian indefinite ending -un',
    'ّ': 'Shadda (doubling) - Persian consonant doubling/emphasis',
    'ْ': 'Sukun (no vowel) - Persian silent consonant marker',
    'ٰ': 'Alif Khanjariya - Persian long a sound',
    'ٱ': 'Alif Wasla - Persian connecting alif'
  };

  const handleKeyClick = (key: string) => {
    onInputChange(currentText + key);
  };

  const handleBackspace = () => {
    onInputChange(currentText.slice(0, -1));
  };

  const handleSpace = () => {
    onInputChange(currentText + ' ');
  };

  const handleEnter = () => {
    onInputChange(currentText + '\n');
  };

  const handleShift = () => {
    setIsShiftPressed(!isShiftPressed);
  };

  const handleAlt = () => {
    setIsAltPressed(!isAltPressed);
  };

  const toggleVowels = () => {
    setShowVowels(!showVowels);
  };

  const getDisplayKeys = () => {
    if (isShiftPressed && isAltPressed) {
      // Show special characters
      return [
        farsiKeys[0], // vowels
        farsiKeys[1],
        farsiKeys[2],
        farsiKeys[3],
        specialChars
      ];
    } else if (isShiftPressed) {
      // Show English numbers
      return [
        farsiKeys[0], // vowels
        farsiKeys[1],
        farsiKeys[2],
        farsiKeys[3],
        englishNumbers
      ];
    } else {
      // Show default Farsi layout
      return farsiKeys;
    }
  };

  const renderVowelKey = (vowel: string) => (
    <button
      key={vowel}
      className="keyboard-key vowel-key"
      onClick={() => handleKeyClick(vowel)}
      title={vowelExplanations[vowel as keyof typeof vowelExplanations]}
    >
      <span className="vowel-symbol">{vowel}</span>
      <span className="vowel-explanation">
        {vowelExplanations[vowel as keyof typeof vowelExplanations]}
      </span>
    </button>
  );

  return (
    <div className="farsi-keyboard-container">
      {/* Text Input Area */}
      <div className="keyboard-input-area">
        <textarea
          value={currentText}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={placeholder}
          className="keyboard-textarea"
          dir="rtl"
          rows={3}
        />
      </div>

      {/* Vowel Toggle */}
      <div className="vowel-toggle">
        <button
          className={`vowel-toggle-btn ${showVowels ? 'active' : ''}`}
          onClick={toggleVowels}
        >
          {showVowels ? '🔤 Hide Persian Vowels' : '🔤 Show Persian Vowels'}
        </button>
      </div>

      {/* Keyboard */}
      <div className="farsi-keyboard">
        {/* Vowel row - conditionally visible */}
        {showVowels && (
          <div className="keyboard-row vowel-row">
            {farsiKeys[0].map((vowel) => renderVowelKey(vowel))}
          </div>
        )}

        {/* Main keys */}
        {getDisplayKeys().slice(1).map((row, rowIndex) => (
          <div key={rowIndex + 1} className="keyboard-row">
            {row.map((key, keyIndex) => (
              <button
                key={keyIndex}
                className="keyboard-key"
                onClick={() => handleKeyClick(key)}
              >
                {key}
              </button>
            ))}
          </div>
        ))}

        {/* Bottom row with special keys */}
        <div className="keyboard-row bottom-row">
          <button
            className="keyboard-key shift-key"
            onClick={handleShift}
            data-active={isShiftPressed}
            title="Shift - Numbers/Symbols"
          >
            {isShiftPressed ? '⇧' : '⇧'}
          </button>
          
          <button
            className="keyboard-key alt-key"
            onClick={handleAlt}
            data-active={isAltPressed}
            title="Alt - Special Characters"
          >
            Alt
          </button>

          <button
            className="keyboard-key space-key"
            onClick={handleSpace}
            title="Space"
          >
            Space
          </button>

          <button
            className="keyboard-key backspace-key"
            onClick={handleBackspace}
            title="Backspace"
          >
            ←
          </button>

          <button
            className="keyboard-key enter-key"
            onClick={handleEnter}
            title="Enter"
          >
            ↵
          </button>
        </div>
      </div>

      {/* Practice Section */}
      <div className="keyboard-practice">
        <h3>Practice Writing with Persian Vowels</h3>
        <div className="practice-suggestions">
          <button
            className="practice-suggestion"
            onClick={() => onInputChange(currentText + 'سَلام')}
            title="Hello (with Persian vowels)"
          >
            سَلام (Hello - Persian)
          </button>
          <button
            className="practice-suggestion"
            onClick={() => onInputChange(currentText + 'چِطوری')}
            title="How are you? (with Persian vowels)"
          >
            چِطوری (How are you? - Persian)
          </button>
          <button
            className="practice-suggestion"
            onClick={() => onInputChange(currentText + 'مَمنون')}
            title="Thank you (with Persian vowels)"
          >
            مَمنون (Thank you - Persian)
          </button>
          <button
            className="practice-suggestion"
            onClick={() => onInputChange(currentText + 'دُوستَت دارَم')}
            title="I love you (with Persian vowels)"
          >
            دُوستَت دارَم (I love you - Persian)
          </button>
        </div>
      </div>
    </div>
  );
};

export default FarsiKeyboard; 